import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Sparkline from '../../../../components/Sparkline/Sparkline';
import { EM_DASH, formatNumber, formatEtDateTime } from './queueMonitorFormat';

/**
 * PerSourceHourlyPanel (v2 contract 7.3)
 * Per-source hourly error-rate panel fed by GET /api/violerts-queue/metrics/hourly
 * (basis "job_metrics_hourly"). Renders one row per source (bis / opendata / other)
 * with a 48h failure-rate sparkline (nulls filtered) plus the current-hour
 * attempts / failures / rate, and a queue filter <select> built from the distinct
 * queues present in rows ('' = the pre-deploy sentinel, labeled "(pre-deploy)").
 *
 * Locked behaviors:
 * - data null (loading / old BE) -> renders nothing.
 * - MANDATED LABEL: header carries "per-queue since {queue_dim_since ET}" whenever
 *   a queue filter is active (per-queue slices displayed); with queue_dim_since
 *   null it says "per-queue dimension not yet recorded".
 * - Basis caption: "hourly grain, attempt-level (job_metrics)".
 * - Rates follow the canonical job_metrics definition (contract 7.1):
 *   failures / attempts, null below the 25-attempt volume floor, 4 decimals;
 *   rendered as percent (2 decimals), em dash for null - never 0% or 100%.
 *   Aggregation across queues/buckets happens here (the BE rows are per
 *   (bucket, source, queue)), so the floor is re-applied to each aggregate.
 */

// Mirrors config('queue_monitor.rates.volume_floor') - the canonical rate floor.
const VOLUME_FLOOR = 25;

// Select sentinel for "no queue filter". A real queue name can be '' (the
// pre-deploy sentinel), so the all-queues option needs a distinct value.
const ALL_QUEUES = '__all__';

const SOURCE_ORDER = ['bis', 'opendata', 'other'];
const SOURCE_LABELS = { bis: 'BIS', opendata: 'Open Data', other: 'Other' };

const PRE_DEPLOY_LABEL = '(pre-deploy)';

const canonicalRate = (failures, attempts) => {
	if (!Number.isFinite(attempts) || attempts < VOLUME_FLOOR) {
		return null;
	}
	return Math.round((failures / attempts) * 10000) / 10000;
};

const formatRatePct = (rate) => {
	if (rate === null || rate === undefined || !Number.isFinite(rate)) {
		return EM_DASH;
	}
	return `${(rate * 100).toFixed(2)}%`;
};

const queueOptionLabel = (queue) => (queue === '' ? PRE_DEPLOY_LABEL : queue);

const PerSourceHourlyPanel = ({ data, isLoading }) => {
	const [selectedQueue, setSelectedQueue] = useState(ALL_QUEUES);

	const rows = useMemo(
		() => (data && Array.isArray(data.rows) ? data.rows : []),
		[data],
	);

	const queues = useMemo(() => {
		const seen = [];
		rows.forEach((row) => {
			if (!row || typeof row.queue !== 'string') {
				return;
			}
			if (!seen.includes(row.queue)) {
				seen.push(row.queue);
			}
		});
		return seen.sort();
	}, [rows]);

	const filteredRows = useMemo(() => {
		if (selectedQueue === ALL_QUEUES) {
			return rows;
		}
		return rows.filter((row) => row && row.queue === selectedQueue);
	}, [rows, selectedQueue]);

	// Aggregate to (source, bucket): sum attempts/failures, recompute the
	// canonical rate (the per-row BE rate is per queue and cannot be averaged).
	const bySource = useMemo(() => {
		const map = {};
		filteredRows.forEach((row) => {
			if (!row || typeof row.bucket !== 'string') {
				return;
			}
			const source = typeof row.source === 'string' ? row.source : 'other';
			if (!map[source]) {
				map[source] = {};
			}
			const agg = map[source][row.bucket] || { attempts: 0, failures: 0 };
			agg.attempts += typeof row.attempts === 'number' ? row.attempts : 0;
			agg.failures += typeof row.failures === 'number' ? row.failures : 0;
			map[source][row.bucket] = agg;
		});
		return map;
	}, [filteredRows]);

	if (!data) {
		// Loading / old backend during deploy skew: render nothing.
		return null;
	}

	const sources = SOURCE_ORDER.filter((source) => bySource[source]).concat(
		Object.keys(bySource)
			.filter((source) => !SOURCE_ORDER.includes(source))
			.sort(),
	);

	const perQueueLabel =
		selectedQueue !== ALL_QUEUES
			? data.queue_dim_since
				? `per-queue since ${formatEtDateTime(data.queue_dim_since)} ET`
				: 'per-queue dimension not yet recorded'
			: null;

	return (
		<div className="analytics-card per-source-hourly-panel" aria-busy={!!isLoading}>
			<div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
				<h5 className="mb-0 me-2">
					Per-source error rate (hourly)
					{perQueueLabel && (
						<span className="text-muted small ms-2 per-queue-since">
							{perQueueLabel}
						</span>
					)}
				</h5>
				{queues.length > 0 && (
					<select
						className="form-select form-select-sm d-inline-block w-auto"
						aria-label="Filter hourly metrics by queue"
						value={selectedQueue}
						onChange={(e) => setSelectedQueue(e.target.value)}
					>
						<option value={ALL_QUEUES}>All queues</option>
						{queues.map((queue) => (
							<option key={`q-${queueOptionLabel(queue)}`} value={queue}>
								{queueOptionLabel(queue)}
							</option>
						))}
					</select>
				)}
			</div>
			{sources.length === 0 ? (
				<p className="text-muted hourly-empty mb-1">
					No hourly metrics in window
				</p>
			) : (
				<div className="table-responsive">
					<table className="table table-sm mb-1">
						<thead className="table-light">
							<tr>
								<th>Source</th>
								<th>Rate trend</th>
								<th className="text-end">Attempts</th>
								<th className="text-end">Failures</th>
								<th className="text-end">Rate</th>
							</tr>
						</thead>
						<tbody>
							{sources.map((source) => {
								const buckets = Object.keys(bySource[source]).sort();
								const rateSeries = buckets
									.map((bucket) => {
										const agg = bySource[source][bucket];
										return canonicalRate(agg.failures, agg.attempts);
									})
									.filter((rate) => typeof rate === 'number');
								const latestBucket = buckets[buckets.length - 1];
								const latest = bySource[source][latestBucket];
								const latestRate = canonicalRate(
									latest.failures,
									latest.attempts,
								);
								return (
									<tr key={source} data-source={source}>
										<td>{SOURCE_LABELS[source] || source}</td>
										<td>
											<Sparkline
												points={rateSeries}
												width={160}
												height={28}
												stroke="#dc3545"
												ariaLabel={`${SOURCE_LABELS[source] || source} hourly failure rate trend`}
											/>
										</td>
										<td className="text-end">
											{formatNumber(latest.attempts)}
										</td>
										<td className="text-end">
											{formatNumber(latest.failures)}
										</td>
										<td className="text-end">{formatRatePct(latestRate)}</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}
			<div className="text-muted small">
				hourly grain, attempt-level (job_metrics); attempts / failures / rate
				columns show the latest UTC hour bucket
			</div>
		</div>
	);
};

PerSourceHourlyPanel.propTypes = {
	data: PropTypes.object, // /metrics/hourly payload; null = loading/old BE (renders nothing)
	isLoading: PropTypes.bool,
};

PerSourceHourlyPanel.defaultProps = {
	data: null,
	isLoading: false,
};

export default PerSourceHourlyPanel;
